import { Menu } from "lucide-react";
import { Link } from "@/lib/nav";
import { usePathname } from "@/lib/nav";
import { ReactNode, useMemo, useState } from "react";

import { BrandLogo } from "@buildpy/ui/brand-logo";
import { BRAND_NAME } from "@buildpy/ui/brand-paths";
import { Button } from "@buildpy/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@buildpy/ui/sheet";
import { Member } from "@buildpy/core/modules/org/types";
import { PLAN_LABEL, type PlanName } from "@buildpy/core/modules/plan/types";
import AppNavList, { NavBadge } from "@/shell/app-nav-list";
import AppRail, { RailMember } from "@/shell/app-rail";
import { useCrumbName } from "@/shell/crumb";
import { findNavMatch, subPageName } from "@/shell/nav";
import SearchPalette from "@/shell/search-palette";
import ThemeSelect from "@/shell/theme-select";

interface Props {
  member: Member;
  /** 로그아웃 서버 액션 참조. 서버 레이아웃이 내려준다. */
  onLogout: () => Promise<void>;
  orgName?: string | null;
  plan?: PlanName | null;
  badges?: Record<string, NavBadge>;
  children: ReactNode;
}

const AppShell = ({ member, onLogout, orgName, plan, badges, children }: Props) => {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const current = useMemo(() => findNavMatch(pathname), [pathname]);
  const subPage = subPageName(pathname);
  const recordName = useCrumbName(pathname);

  return (
    <div className="flex min-h-screen flex-1 bg-background text-foreground">
      <AppRail
        badges={badges}
        className="hidden md:flex"
        member={member}
        orgName={orgName}
        plan={plan}
        onLogout={onLogout}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* 앱 껍데기·노치 아래로 내린다 — 줄 높이는 box-content 로 그대로(47px + 테두리 1px). */}
        <header className="sticky top-0 z-20 box-content flex h-11.75 shrink-0 items-center gap-x-2 border-b border-border bg-card px-3 pt-safe">
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="메뉴 열기"
                className="md:hidden [&_svg]:size-5"
                size="icon"
                variant="ghost-muted">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent
              className="flex w-56 flex-col p-0"
              side="left"
              variant="rail">
              {/* 시트 조각에는 안전 영역 클래스를 못 얹어(no-restyle) 빈 줄로 상태바 아래로 내린다. */}
              <div aria-hidden className="shrink-0 pt-safe" />
              <SheetHeader className="shrink-0 px-4 pt-4" isClose>
                <div className="flex min-w-0 flex-col">
                  <SheetTitle className="text-left text-sm">
                    <BrandLogo size="sm" tone="rail" />
                  </SheetTitle>
                  {orgName && (
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-rail-muted" data-testid="sheet-org-name">
                      {orgName}
                    </span>
                  )}
                  {plan && (
                    <span className="text-xs text-rail-muted" data-testid="sheet-plan">
                      {PLAN_LABEL[plan]}
                    </span>
                  )}
                  <SheetDescription className="sr-only">이동할 화면을 고르거나 로그아웃합니다</SheetDescription>
                </div>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-3">
                <AppNavList
                  badges={badges}
                  onNavigate={() => setIsMenuOpen(false)}
                />
              </div>
              <RailMember className="mx-2.5 shrink-0 pb-safe-3" member={member} onLogout={onLogout} />
            </SheetContent>
          </Sheet>

          {/* 위아래 여백은 링크의 넓힌 누르는 영역이 overflow 에 잘리지 않게 — 줄 높이는 그대로. */}
          <span className="-my-3 overflow-hidden text-ellipsis whitespace-nowrap py-3 text-sm text-muted-foreground">
            {current && (subPage || recordName) ? (
              <>
                {current.group} /{" "}
                <Link className="tap-target relative hover:text-foreground hover:underline" href={current.item.href}>
                  {current.item.name}
                </Link>{" "}
                / <b className="font-semibold text-foreground" data-testid="crumb-last">{subPage ?? recordName}</b>
              </>
            ) : current ? (
              <>
                {current.group} /{" "}
                <b className="font-semibold text-foreground">{current.item.name}</b>
              </>
            ) : (
              BRAND_NAME
            )}
          </span>

          <div className="ml-auto flex items-center gap-x-1">
            <SearchPalette />
            <ThemeSelect />
          </div>
        </header>

        <main className="flex min-w-0 flex-1 flex-col gap-y-4 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
